/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

const baseURL = import.meta.env.VITE_APP_API_BE_URL;

// Define the expected response structure
export interface ApiResponse<T, E> {
  apiVersion: string;
  data?: T;
  error?: E;
  path: string;
}

// Create an axios instance
const api = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 100000,
});

// Function to get tokens from local storage
const getTokens = () => {
  return {
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken'),
  };
};

// Function to set tokens in local storage
export const setTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
};

// Function to refresh the access token
const refreshAccessToken = async () => {
  const { refreshToken } = getTokens();
  if (!refreshToken) throw new Error('No refresh token available');

  const response = await api.post('refresh', { refreshToken });
  const { accessToken, refreshToken: newRefreshToken } = response.data;

  setTokens(accessToken, newRefreshToken);
  return accessToken;
};

// Function to handle API requests with token management
const apiRequest = async (method: 'get' | 'post' | 'put' | 'patch' | 'delete', url: string, options?: AxiosRequestConfig): Promise<ApiResponse<any, any>> => {
  const { accessToken } = getTokens();
  const headers = {
    ...options?.headers,
    Authorization: `Bearer ${accessToken}`,
  };

  try {
    const response: AxiosResponse = await api.request({ method, url, headers, ...options });
    return response.data; // Return the successful response
  } catch (error: any) {
    // Handle the error and check for 401 status
    if (error.response?.status === 401) {
      const newAccessToken = await refreshAccessToken();
      headers.Authorization = `Bearer ${newAccessToken}`;
      const response: AxiosResponse = await api.request({ method, url, headers, ...options });
      return response.data; // Return the response after refreshing the token
    }
    // Return a structured error response
    return {
      apiVersion: '1.0', // Adjust as necessary
      error: {
        code: error.response?.data?.error?.code,
        message: error.response?.data?.error?.message || 'An error occurred',
      },
      path: url,
    };
  }
};

export const apiClient = {
  get: async <T, E = any>(url: string, options?: AxiosRequestConfig): Promise<ApiResponse<T, E>> => {
    const response = await apiRequest('get', url, options);
    return response as ApiResponse<T, E>;
  },

  post: async <T, E = any>(url: string, data?: unknown, options?: AxiosRequestConfig): Promise<ApiResponse<T, E>> => {
    const response = await apiRequest('post', url, { data, ...options });
    return response as ApiResponse<T, E>;
  },

  put: async <T, E = any>(url: string, data?: unknown, options?: AxiosRequestConfig): Promise<ApiResponse<T, E>> => {
    const response = await apiRequest('put', url, { data, ...options });
    return response as ApiResponse<T, E>;
  },

  patch: async <T, E = any>(url: string, data?: unknown, options?: AxiosRequestConfig): Promise<ApiResponse<T, E>> => {
    const response = await apiRequest('patch', url, { data, ...options });
    return response as ApiResponse<T, E>;
  },

  delete: async <T, E = any>(url: string, options?: AxiosRequestConfig): Promise<ApiResponse<T, E>> => {
    const response = await apiRequest('delete', url, options);
    return response as ApiResponse<T, E>;
  },
};

export default api;
